import React, { useEffect, useState } from 'react'
import { ArrayField, FormProvider, useFieldArray, useWatch } from 'react-hook-form'
import Button from 'transfix-ui/components/Button'
import { makeStyles, Typography } from 'transfix-ui/core'
import {
  IIssueAutomationRuleActionEnum,
  IIssueAutomationRuleNodeTypeEnum,
  IShipmentStates,
} from 'types/graphqlTypes'
import { brandColors, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { IIssueAutomationError } from '../AutomationPage'
import { IIssueAutomationRuleFragment } from '../graphql/IssueAutomationRuleFragment'
import { getDefaultRuleNodesValues, getSameDayMessage } from '../utils'
import { TIssueRuleFormFormSchema, useIssueRuleForm } from './RuleForm'
import SelectTriggerEvent from './SelectTriggerEvent'
import ShipmentEventCard from './ShipmentEventCard'
import ShipmentInformationCard from './ShipmentInformationCard'
import ShipmentStatusCard from './ShipmentStatusCard'
import TimeWindowCard from './TimeWindowCard'

const useStyles = makeStyles(theme => ({
  container: {
    height: '100vh',
    overflow: 'scroll',
    borderLeft: `1px solid ${brandColors.coolGray4}`,
    backgroundColor: brandColors.coolGray0,
  },
  form: {
    backgroundColor: brandColors.white,
    padding: theme.spacing(2),
  },
  buttons: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    '& > button:first-child': {
      marginRight: theme.spacing(1),
    },
  },
  addTriggerButton: {
    marginTop: theme.spacing(1),
  },
  errorMessage: {
    marginTop: theme.spacing(1),
    padding: `${theme.spacing(2)} ${theme.spacing(2)} 0`,
  },
}))

export interface IRuleFormSectionProps {
  ruleType: IIssueAutomationRuleActionEnum
  onCancel: () => void
  onSubmit: (formData: TIssueRuleFormFormSchema) => void
  selectedRule: IIssueAutomationRuleFragment | null
  disabled: boolean
  error: IIssueAutomationError | undefined
  warning: IIssueAutomationError | undefined
}

function RuleFormSection({
  disabled,
  error,
  onCancel,
  onSubmit,
  ruleType,
  selectedRule,
  warning,
}: IRuleFormSectionProps) {
  const classes = useStyles()

  const [showTriggerSelector, setShowTriggerSelector] = useState<boolean>(!selectedRule)
  const currentRuleType = selectedRule?.ruleAction || ruleType

  const methods = useIssueRuleForm({
    defaultValues: {
      ruleType,
      ...getDefaultRuleNodesValues(selectedRule),
    },
    shouldUnregister: false,
  })

  const nodesWatch:
    | Partial<ArrayField<TIssueRuleFormFormSchema['nodes'][number], 'key'>>[]
    | undefined = useWatch({
    control: methods.control,
    name: 'nodes',
  })

  const { append: appendNode, fields: nodesFields, remove: removeNode } = useFieldArray({
    control: methods.control,
    name: 'nodes',
    keyName: 'key',
  })
  const selectedNodeTypes = nodesFields.map(nodeField => {
    if (nodeField.isShipmentStatus) {
      return IIssueAutomationRuleNodeTypeEnum.shipment_status
    }
    if (nodeField.isTimeWindow) {
      return IIssueAutomationRuleNodeTypeEnum.time_window
    }
    if (nodeField.isShipmentEvent) {
      return IIssueAutomationRuleNodeTypeEnum.shipment_event
    }
    return IIssueAutomationRuleNodeTypeEnum.shipment_information
  })

  const selectedRuleId = selectedRule?.id || ''

  // Reset form with default values when selectedRuleId changes
  useEffect(() => {
    if (selectedRuleId) {
      const { nodes: defaultNodes } = getDefaultRuleNodesValues(selectedRule)
      methods.reset({
        ruleType,
        nodes: defaultNodes,
      })
      setShowTriggerSelector(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRuleId])

  const warningKey = warning?.key
  // Check if shipment status warning message is thrown and remove invalid status
  useEffect(() => {
    if (warningKey === 'SHIPMENT_STATUS_WARNING') {
      const shipmentStatusNodeIndex = nodesWatch?.findIndex(node => node.isShipmentStatus) ?? -1
      if (shipmentStatusNodeIndex !== -1) {
        // There should be a shipment status, but check just in case there isn't
        const newShipmentStatusFields =
          nodesWatch?.[shipmentStatusNodeIndex].shipmentStatusFields?.filter(
            status => ![IShipmentStates.canceled, IShipmentStates.completed].includes(status)
          ) || []
        methods.setValue(
          `nodes[${shipmentStatusNodeIndex}].shipmentStatusFields`,
          newShipmentStatusFields
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningKey])

  const { handleSubmit, register } = methods

  const isOpenRule = currentRuleType === IIssueAutomationRuleActionEnum.create
  const hasNodeTypesUnselected =
    nodesFields.length < Object.values(IIssueAutomationRuleNodeTypeEnum).length + 1

  const sameDayMessage = getSameDayMessage(nodesWatch)

  const handleAddNode = (newNodeType: IIssueAutomationRuleNodeTypeEnum) => {
    if (newNodeType === IIssueAutomationRuleNodeTypeEnum.shipment_information) {
      appendNode({
        isShipmentInformation: true,
      })
    } else if (newNodeType === IIssueAutomationRuleNodeTypeEnum.shipment_status) {
      appendNode({
        isShipmentStatus: true,
        shipmentStatusOperator: 'is-any',
      })
    } else if (newNodeType === IIssueAutomationRuleNodeTypeEnum.time_window) {
      appendNode({
        isTimeWindow: true,
      })
    } else if (newNodeType === IIssueAutomationRuleNodeTypeEnum.shipment_event) {
      appendNode({
        isShipmentEvent: true,
      })
    }
    setShowTriggerSelector(false)
  }

  const handleRemoveNode = (index: number) => {
    removeNode(index)
    if (index === 0) {
      // If it's the last node, show trigger select
      setShowTriggerSelector(true)
    }
  }

  return (
    <div className={classes.container}>
      <FormProvider {...methods}>
        <form>
          <input type='hidden' name='ruleType' ref={register()} />
          <div className={classes.form}>
            <Typography variant='subtitle1'>{isOpenRule ? 'Open' : 'Close'} Issue When:</Typography>
            {nodesFields.map((nodeField, nodeIndex) => {
              const isFinalNode = nodeIndex === nodesFields.length - 1
              if (nodeField.isShipmentStatus) {
                return (
                  <ShipmentStatusCard
                    onAddTrigger={() => setShowTriggerSelector(true)}
                    isAddTriggerDisabled={
                      !hasNodeTypesUnselected || showTriggerSelector || !isFinalNode
                    }
                    onRemove={() => handleRemoveNode(nodeIndex)}
                    showClearButton={showTriggerSelector}
                    onCancel={() => setShowTriggerSelector(false)}
                    index={nodeIndex}
                    node={nodeField}
                    key={nodeField.key}
                  />
                )
              }
              if (nodeField.isTimeWindow) {
                return (
                  <TimeWindowCard
                    onAddTrigger={() => setShowTriggerSelector(true)}
                    isAddTriggerDisabled={
                      !hasNodeTypesUnselected || showTriggerSelector || !isFinalNode
                    }
                    onRemove={() => handleRemoveNode(nodeIndex)}
                    showClearButton={showTriggerSelector}
                    onCancel={() => setShowTriggerSelector(false)}
                    index={nodeIndex}
                    key={nodeField.key}
                  />
                )
              }
              if (nodeField.isShipmentInformation) {
                return (
                  <ShipmentInformationCard
                    onAddTrigger={() => setShowTriggerSelector(true)}
                    isAddTriggerDisabled={
                      !hasNodeTypesUnselected || showTriggerSelector || !isFinalNode
                    }
                    onRemove={() => handleRemoveNode(nodeIndex)}
                    showClearButton={showTriggerSelector}
                    onCancel={() => setShowTriggerSelector(false)}
                    index={nodeIndex}
                    node={nodeField}
                    key={nodeField.key}
                  />
                )
              }
              if (nodeField.isShipmentEvent) {
                return (
                  <ShipmentEventCard
                    onAddTrigger={() => setShowTriggerSelector(true)}
                    isAddTriggerDisabled={
                      !hasNodeTypesUnselected || showTriggerSelector || !isFinalNode
                    }
                    onRemove={() => handleRemoveNode(nodeIndex)}
                    showClearButton={showTriggerSelector}
                    onCancel={() => setShowTriggerSelector(false)}
                    index={nodeIndex}
                    node={nodeField}
                    key={nodeField.key}
                  />
                )
              }
              return null
            })}
            {showTriggerSelector && (
              <SelectTriggerEvent onSelect={handleAddNode} selectedNodeTypes={selectedNodeTypes} />
            )}
          </div>
          {sameDayMessage && (
            <div className={classes.errorMessage}>
              <InlineMessage
                fullWidth
                message={sameDayMessage}
                type={INLINE_MESSAGE_VARIANTS.INFO}
                dataTest='form-message'
              />
            </div>
          )}
          {error && (
            <div className={classes.errorMessage}>
              <InlineMessage
                fullWidth
                title={error.title}
                message={error.message}
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                dataTest='form-error'
              />
            </div>
          )}
          {warning && (
            <div className={classes.errorMessage}>
              <InlineMessage
                fullWidth
                title={warning.title}
                message={warning.message}
                type={INLINE_MESSAGE_VARIANTS.WARNING}
                dataTest='form-error'
              />
            </div>
          )}
          <div className={classes.buttons}>
            <Button color='tertiary' onClick={onCancel}>
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleSubmit(onSubmit)}
              disabled={!nodesFields.length || disabled}>
              {selectedRule ? 'Save' : 'Save as'}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default RuleFormSection
